import { useCallback, useEffect, useRef, useState } from 'react';

export const isSectionVisible = (elementId: string, offset = 0): boolean => {
  const section = document.getElementById(elementId);
  if (section) {
    const sectionPosition = section.getBoundingClientRect();

    const viewPort = {
      height: window.innerHeight,
      width: window.innerWidth,
    };

    return (
      sectionPosition.bottom > offset &&
      sectionPosition.top < viewPort.height - offset
    );
  }

  return false;
};

export interface VisibilitySection {
  id: string;
  title: string;
}

interface UseVisibleSectionHook {
  visibleSectionId: string;
  pauseTracking: () => void;
  resumeTracking: () => void;
}

export const useVisibleSection = (
  sections: VisibilitySection[],
  offset: number = 0,
): UseVisibleSectionHook => {
  const [visibleSectionId, setVisibleSectionId] = useState<string>('');
  const isTrackingRef = useRef(true);

  const checkVisibility = useCallback(() => {
    if (!isTrackingRef.current) return;

    for (const { id } of sections) {
      if (isSectionVisible(id, offset)) {
        setVisibleSectionId(id);
        break;
      }
    }
  }, [sections, offset]);

  useEffect(() => {
    window.addEventListener('scroll', checkVisibility);

    checkVisibility();

    return () => {
      window.removeEventListener('scroll', checkVisibility);
    };
  }, [checkVisibility]);

  const pauseTracking = () => {
    isTrackingRef.current = false;
  };

  const resumeTracking = () => {
    isTrackingRef.current = true;
    checkVisibility();
  };

  return { visibleSectionId, pauseTracking, resumeTracking };
};
